import { LabelWrapper } from '@gameday/react-components/components'
import { connect, Formik } from 'formik'
import FormikErrorFocus from 'formik-error-focus'
import { isEmpty } from 'lodash'
import { useEffect, useState } from 'react'
import { object } from 'yup'

import { displayRuleFilter } from '../../../../factories/filter'
import productsListHook from '../../../../hooks/productsList'
import NavigationButtons from '../../Common/NavigationButton'
import ProductsList from '../Products/productsList'
import { depFilter, FormikEffectComp, noShopMessage } from '../Products/utils'

const FormikEffect = connect(FormikEffectComp)

const AddOnProductsComponent = ({
  addOn,
  onSelected,
  buttonColour,
  buttonTextColour,
  previous,
  formType,
  formData,
  next,
  paidProducts,
  preselectedProduct,
  initialValues,
  formError,
  feeConfigurations,
  paymentGateway,
  hasSubEvents,
  mainProducts,
  mainProductGroups,
  team,
  isTeamRego,
  formInfo,
  displayPriceAsZero = false,
}) => {
  let submit = () => {}
  const { products: addOnProds = [], productGroups: addOnProductGroups = [] } = addOn
  const { products: teamOnProds = [], productGroups: teamOnProductGroups = [] } = team
  const products = [...mainProducts, ...addOnProds]
  const productGroups = [...mainProductGroups, ...addOnProductGroups]

  if (isTeamRego) {
    products.push([...teamOnProds])
    productGroups.push([...teamOnProductGroups])
  }

  const isShopForm = formInfo?.form?.type?.toLowerCase() === 'shop' || formType?.toLowerCase() === 'shop'

  // biome-ignore lint/performance/noAccumulatingSpread: TODO
  const paidMap = paidProducts.reduce((a, p) => ({ ...a, [p.sfid]: p.quantity }), {})

  const [isContinueDisabled, setContinueDisabled] = useState(true)
  const filteredProducts = displayRuleFilter(
    addOnProds.map(p => ({ ...p, org: p.definedById })),
    formData,
    formType === 'event'
  )
  const [count, setCount] = useState(0)
  const [pgMinimumFulfilled, setMinimumFulfilled] = useState(
    productGroups.reduce((a, pg) => {
      if (Number(pg.minimumProductCount) > 0) {
        a[pg.id] = false
      }
      return a
    }, {})
  )

  const [pgMaximumFulfilled, setMaximumFulfilled] = useState(
    productGroups.reduce((a, pg) => {
      if (Number(pg.maximumProductCount) > 0) {
        a[pg.id] = false
      }
      return a
    }, {})
  )

  useEffect(() => {
    setCount(depFilter(filteredProducts, paidMap, formData.newProducts).length)
  }, [])

  const { initValues, schema, childProductList, errorType, pgClone, groupMap, formProductIdMap, errorText } =
    productsListHook({
      paidMap,
      filteredProducts,
      productGroups,
      preselectedProduct,
      initialValues,
      isForSubEvents: false,
    })

  const [displayGroupMap, setDisplayGroupMap] = useState({
    groupMap,
  })
  // preselected product
  const psProduct = filteredProducts.find(p => p.sfid === preselectedProduct)

  const [showUnpurchasable, setShowUnpurchasable] = useState(false)

  const renderProductList = (errors, touched, values, onProductChange) =>
    pgClone
      .sort((a, b) => {
        const orgLevelA = a.organisationLevel
        const orgLevelB = b.organisationLevel
        if (orgLevelA !== orgLevelB) {
          return orgLevelA - orgLevelB
        }
        const lockA = a.isLocked ? 0 : 1
        const lockB = b.isLocked ? 0 : 1
        if (lockA !== lockB) {
          return lockA - lockB
        }
        return a.orderNumber - b.orderNumber
      })
      .map(pg => {
        const productsInGroup = displayGroupMap[pg.id]
        if ((productsInGroup || []).length < 1) {
          return null
        }
        return (
          // eslint-disable-next-line react/jsx-key
          // biome-ignore lint/correctness/useJsxKeyInIterable: TODO
          <ProductsList
            pg={pg}
            psProduct={psProduct}
            paidMap={paidMap}
            formType={formType}
            onProductChange={onProductChange}
            childProductList={childProductList}
            errors={errors}
            errorType={errorType}
            errorText={errorText}
            touched={touched}
            values={values}
            pgMinimumFulfilled={pgMinimumFulfilled}
            pgMaximumFulfilled={pgMaximumFulfilled}
            productsInGroup={productsInGroup}
            feeConfigurations={feeConfigurations}
            paymentGateway={paymentGateway}
            formData={formData}
            displayPriceAsZero={displayPriceAsZero}
          />
        )
      })

  return (
    <>
      <h2>Select your Add-ons</h2>
      {count < 1 && <LabelWrapper errorText={noShopMessage} />}
      {formError && <LabelWrapper errorText={formError} />}
      {showUnpurchasable && (
        <LabelWrapper
          errorText={`${
            psProduct ? psProduct.name : 'The product you selected '
          } is currently unavailable for selection due to member restrictions.`}
        />
      )}
      <Formik
        onSubmit={values => {
          onSelected(values)
          next()
        }}
        initialValues={initValues}
        enableReinitialize
        validationSchema={object(schema)}
      >
        {({ errors, touched, values, submitForm, setFieldValue, setFieldTouched }) => {
          submit = submitForm

          const onProductChange = (key, value) => {
            setFieldTouched(key, true)
            const formProductId = formProductIdMap[key]
            setFieldValue(key, { ...value, formProductId })
          }

          return (
            <form>
              {renderProductList(errors, touched, values, onProductChange)}
              <FormikEffect
                setContinueDisabled={setContinueDisabled}
                preselectedProduct={preselectedProduct}
                hasPreselectedProduct={!!preselectedProduct}
                setShowUnpurchasable={setShowUnpurchasable}
                setMinimumFulfilled={setMinimumFulfilled}
                setMaximumFulfilled={setMaximumFulfilled}
                productGroups={productGroups}
                paidProducts={paidMap}
                products={products}
                groupMap={{ ...groupMap }}
                setDisplayGroupMap={setDisplayGroupMap}
                hasSubEvents={hasSubEvents}
                allowSkip={
                  isShopForm
                    ? false
                    : isEmpty(addOn.products) ||
                      (addOn.products.every(p => !p.isMandatory) &&
                        !addOn.productGroups.some(pg => pg.minimumProductCount > 0))
                }
                parentValues={formData.newProducts}
              />
              <FormikErrorFocus
                // See scroll-to-element for configuration options: https://www.npmjs.com/package/scroll-to-element
                offset={-150}
                align="top"
                focusDelay={0}
                ease="in-out-quad"
                duration={250}
              />
            </form>
          )
        }}
      </Formik>

      <NavigationButtons
        buttonColour={buttonColour}
        buttonTextColour={buttonTextColour}
        isContinueDisabled={isContinueDisabled}
        onSubmit={() => {
          submit()
        }}
        onBack={() => {
          previous()
        }}
      />
    </>
  )
}

export default AddOnProductsComponent
